"use client";

import { useState } from "react";
import dynamic from "next/dynamic";
import Button from "@ishvaram/ui/Button";
import { useAuth } from "@/components/AuthProvider";
import type { User } from "@supabase/supabase-js";

const LoginModal = dynamic(() => import("./LoginModal"), { ssr: false });

interface HeaderAuthProps {
  className?: string;
  fullWidth?: boolean;
}

function accountLabel(userName?: string | null) {
  if (!userName) return "Account";
  return userName.split("@")[0] || "Account";
}

function userDisplayName(user: User): string | undefined {
  if (typeof user.user_metadata?.full_name === "string") {
    return user.user_metadata.full_name;
  }
  if (typeof user.user_metadata?.name === "string") {
    return user.user_metadata.name;
  }
  return user.email || user.phone || undefined;
}

export default function HeaderAuth({ className = "", fullWidth = false }: HeaderAuthProps) {
  const [showLogin, setShowLogin] = useState(false);
  const { user, loading, signOut } = useAuth();

  if (loading) {
    return <AuthLoadingSlot className={className} fullWidth={fullWidth} />;
  }

  if (user) {
    return (
      <SignedInAuthMenu
        className={className}
        displayName={userDisplayName(user)}
        fullWidth={fullWidth}
        onSignOut={signOut}
      />
    );
  }

  return (
    <>
      <Button
        variant="secondary"
        title="Sign in"
        onPress={() => setShowLogin(true)}
        className={`!py-2 !px-4 !text-[13px] !min-h-[40px] ${fullWidth ? "!w-full" : ""} ${className}`}
      />
      {showLogin && <LoginModal isOpen={showLogin} onClose={() => setShowLogin(false)} />}
    </>
  );
}

function AuthLoadingSlot({ className, fullWidth }: Required<HeaderAuthProps>) {
  return (
    <span
      aria-hidden
      className={`inline-flex h-10 rounded-lg border ${fullWidth ? "w-full" : "w-[84px]"} ${className}`}
      style={{ borderColor: "var(--ish-border)" }}
    />
  );
}

function SignedInAuthMenu({
  className,
  displayName,
  fullWidth,
  onSignOut,
}: {
  className: string;
  displayName?: string;
  fullWidth: boolean;
  onSignOut: () => Promise<void>;
}) {
  return (
    <details className={`relative ${fullWidth ? "w-full" : ""} ${className}`}>
      <summary
        className={`list-none cursor-pointer inline-flex items-center justify-center rounded-lg border px-3 py-2 text-[13px] font-semibold transition hover:bg-[var(--ish-accent-light)] ${
          fullWidth ? "w-full" : "min-h-[40px]"
        }`}
        style={{ color: "var(--ish-text)", borderColor: "var(--ish-border)" }}
      >
        {accountLabel(displayName)}
      </summary>
      <div
        className={`absolute right-0 top-full mt-2 rounded-lg border p-2 shadow-xl z-50 ${
          fullWidth ? "w-full" : "w-56"
        }`}
        style={{ background: "var(--ish-bg, #fff)", borderColor: "var(--ish-border)" }}
      >
        {displayName && (
          <p className="px-2 pb-2 text-[12px] truncate" style={{ color: "var(--ish-text-muted)" }}>
            {displayName}
          </p>
        )}
        <button
          type="button"
          onClick={() => void onSignOut()}
          className="w-full rounded-md px-2 py-2 text-left text-[13px] font-medium hover:bg-[var(--ish-accent-light)]"
          style={{ color: "var(--ish-text)" }}
        >
          Sign out
        </button>
      </div>
    </details>
  );
}
